import React from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";

// Shared layout and UI primitives used by every section.

export const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 640px) {
    padding: 0 16px;
  }
`;

const sectionTones = {
  default: "var(--color-bg)",
  subtle: "var(--color-bg-subtle)",
  tint: "var(--color-bg-tint)",
};

export const Section = styled.section`
  padding: 112px 0;
  background: ${({ $tone = "default" }) => sectionTones[$tone]};

  @media (max-width: 768px) {
    padding: 72px 0;
  }
`;

export const Reveal = ({ as = "div", delay = 0, children, ...rest }) => {
  const Component = motion[as];
  return (
    <Component
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }}
      {...rest}
    >
      {children}
    </Component>
  );
};

export const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-primary);

  &::before {
    content: "";
    width: 20px;
    height: 2px;
    background: currentColor;
  }
`;

export const SectionTitle = styled.h2`
  font-size: clamp(2rem, 3.4vw, 2.75rem);
`;

export const Lead = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  color: var(--color-muted);
  max-width: 560px;
`;

const HeaderWrap = styled(Reveal)`
  display: grid;
  gap: 24px;
  margin-bottom: 56px;

  ${({ $align }) =>
    $align === "center"
      ? css`
          justify-items: center;
          text-align: center;

          ${Lead} {
            margin: 0 auto;
          }
        `
      : css`
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
          align-items: end;
          column-gap: 64px;

          @media (max-width: 900px) {
            grid-template-columns: 1fr;
          }
        `}

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

export const SectionHeader = ({ eyebrow, title, lead, align = "split" }) => (
  <HeaderWrap $align={align}>
    <div>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <SectionTitle>{title}</SectionTitle>
    </div>
    {lead && <Lead>{lead}</Lead>}
  </HeaderWrap>
);

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 22px;
  border: 1px solid transparent;
  border-radius: var(--radius);
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    transition: transform 0.2s var(--ease-out);
  }

  &:hover svg[data-arrow] {
    transform: translateX(3px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const buttonVariants = {
  primary: css`
    background: var(--color-primary);
    color: var(--color-on-primary);

    &:hover:not(:disabled) {
      background: var(--color-primary-hover);
    }
  `,
  secondary: css`
    background: var(--color-surface);
    color: var(--color-ink);
    border-color: var(--color-border-strong);

    &:hover:not(:disabled) {
      border-color: var(--color-ink);
    }
  `,
};

export const Button = styled.button`
  ${buttonBase}
  ${({ $variant = "primary" }) => buttonVariants[$variant]}
`;

export const ButtonLink = styled.a`
  ${buttonBase}
  ${({ $variant = "primary" }) => buttonVariants[$variant]}
`;

export const IconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--radius);
  background: var(--color-primary-soft);
  color: var(--color-primary);

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const TagList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Tag = styled.li`
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-subtle);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--color-text);
`;
